// Mississippi State University: public college page (#2743), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - MSU's Common Data Set 2025-2026 (cds2025_2026.xlsx), the file the
//    Office of Institutional Research and Effectiveness links from its
//    Common Data Sets page: C1 applicants, admits and enrollees for Fall 2025
//    with the residency table, C8 test policy, C21/C22 (no early decision, no
//    early action). Its G1/G5 costs are for 2025-2026, so they are not used.
//  - MSU Undergraduate Admissions, Freshman Admissions (test-optional policy,
//    superscoring, rolling admission, application dates, decision timing),
//    and Scholarships (General Scholarship Application dates for summer/fall
//    2027 entry and the ACT science change for scholarship superscores).
//    MSU does not publish set decision dates: most applicants hear within
//    2 to 3 weeks of a complete file.
//  - MSU Student Financial Aid, Yearly Undergraduate Cost of Attendance for
//    Fall 2026 and Spring 2027, Starkville Main Campus, living on campus.
//    The page's own totals ($34,431 and $53,422) equal the sum of its items.
//  - College Scorecard, school id 176080 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Quirk: the Freshman Admissions FAQ says Mississippi residents with the
// college preparatory curriculum and a 2.0 GPA are admissible, while the
// residents section of the same page lists a 3.20 GPA among several routes.
// The page here states no GPA floor for that reason.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Mississippi State Common Data Set 2025-2026',
        url: 'https://www.ir.msstate.edu/cdsets.php',
        read: '2026-10-01',
    },
    freshman: {
        label: 'MSU Undergraduate Admissions, Freshman Admissions',
        url: 'https://www.admissions.msstate.edu/apply/admission-process/freshman-admissions',
        read: '2026-10-01',
    },
    scholarships: {
        label: 'MSU Undergraduate Admissions, Scholarships',
        url: 'https://www.admissions.msstate.edu/scholarships',
        read: '2026-10-01',
    },
    costs: {
        label: 'MSU Student Financial Aid, Yearly Undergraduate Cost of Attendance, Fall 2026 and Spring 2027',
        url: 'https://www.sfa.msstate.edu/cost/24/ug',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?176080-Mississippi-State-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'mississippi-state-university',
    kbId: 'mississippi_state_university',
    name: 'Mississippi State University',
    shortName: 'Mississippi State',
    location: 'Starkville, MS',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (MSU brand assets page); decoration only.
    branding: {
        primary: '#5D1725',
        secondary: '#FFFFFF',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/mississippi_state_university_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 27333,
        admitted: 22858,
        enrolled: 4049,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 7555, admitted: 6530 },
            { group: 'Out-of-state', applied: 18098, admitted: 15292 },
            { group: 'International', applied: 1680, admitted: 1036 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'MSU\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early plans',
        note: 'MSU has no early decision or early action plan. Admission is rolling: applications are read as they are completed.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Admission', value: 'Rolling' },
        rows: [
            { plan: 'Rolling admission', deadline: 'Open until the 10th day of classes', decision: 'Most applicants within 2 to 3 weeks of a complete file', binding: false },
        ],
        note: 'The application for summer and fall entry opens August 1. MSU encourages you to apply early, since admission is granted on a rolling basis. You can submit the application before your transcript, fee or test scores arrive, but MSU reviews your file for admission and scholarships only once everything is in. The application fee is $50 ($60 for international students).',
        source: 'freshman',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'MSU is test-optional for freshman admission: if you do not send ACT or SAT scores, you are reviewed on high school performance, placement testing and other factors. Scores are still encouraged for scholarships and placement. If you send them, MSU superscores within the ACT or within the SAT, not across them, and its ACT superscore leaves out the Science section. Non-residents who do not meet the 2.50 GPA minimum need a test score: the page says "ACT or SAT score of 20 or above". Home-schooled applicants send ACT or SAT scores.',
        source: 'freshman',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as considered if submitted, not required.',
        cdsWhere: 'section C8',
    },

    // Announced changes already in effect for this cycle's applicants.
    currentChanges: [
        { topic: 'Scholarships', line: 'Beginning in 2027, MSU no longer counts the ACT science score in the superscore it uses for scholarship eligibility. The General Scholarship Application opens October 1, 2026, for students entering in summer or fall 2027, and the priority deadline for entering freshmen is December 1.', source: 'scholarships' },
    ],

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'freshman',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition', amounts: [10845, 29836] },
            { item: 'Required fees (estimate)', amounts: [250, 250] },
            { item: 'Housing (on campus)', amounts: [9990, 9990] },
            { item: 'Food', amounts: [5210, 5210] },
            { item: 'Books and supplies', amounts: [1200, 1200] },
            { item: 'Transportation', amounts: [3132, 3132] },
            { item: 'Personal expenses', amounts: [3804, 3804] },
        ],
        totals: [34431, 53422],
        note: 'For an undergraduate living on campus at the Starkville main campus, fall 2026 and spring 2027. Federal loan fees are added to your own cost of attendance individually.',
        source: 'costs',
        netPrice: {
            average: 18781,
            byIncome: [
                { income: '$0 to $30,000', amount: 17042 },
                { income: '$30,001 to $48,000', amount: 15564 },
                { income: '$48,001 to $75,000', amount: 18211 },
                { income: '$75,001 to $110,000', amount: 20859 },
                { income: 'Over $110,000', amount: 24032 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from MSU\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
